/**
 * SessionDetailFrame: the display half of the service and event detail pages' scroll-over-hero
 * scaffold, shared by the Mini App and the studio website (shared-client-screens D2, rows S6, E6).
 * A fixed back button and the visually hidden page title, a pinned hero, the rising sheet and the
 * fixed bottom action bar, with the browser-only `client-lock` harden. ServiceDetailScreen and
 * EventDetailScreen draw it; the Mini App's data parts add their Telegram parts (the safe-area vars
 * and the mount-scoped `data-client-theme`, useSessionDetailShell).
 */
import type { ReactNode } from 'react';
import { FlowBackButton } from '@/components/booking/FlowBackButton';
import { useClientLock } from '@/hooks/useClientLock';
import styles from './SessionDetailPage.module.css';

export type DetailShellStatus = 'loading' | 'notfound' | 'ready';

export interface SessionDetailFrameProps {
  status: DetailShellStatus;
  pageTitle: string;
  onBack: () => void;
  /** ready: the pinned hero node */
  hero?: ReactNode;
  /** ready: the fixed bottom action bar node */
  bottomBar?: ReactNode;
  /** ready: the sheet body sections */
  children?: ReactNode;
  notFoundMessage?: string;
}

export function SessionDetailFrame({
  status,
  pageTitle,
  onBack,
  hero,
  bottomBar,
  children,
  notFoundMessage = "We couldn't find this — it may have been removed.",
}: SessionDetailFrameProps) {
  // iOS scroll-on-focus harden (the ClientShell's .client-lock).
  useClientLock();

  return (
    <div className={styles.page}>
      <FlowBackButton className={styles.back} onClick={onBack} />
      {/* Nav title is visually hidden — the hero name is the page heading.
          Kept (sr-only) for screen-reader page context. */}
      <p className={styles.srOnly}>{pageTitle}</p>

      {status === 'loading' && (
        <div className={styles.stateWrap} data-testid="detail-loading">
          <div className={styles.spinner} />
        </div>
      )}

      {status === 'notfound' && (
        <div className={styles.stateWrap} data-testid="detail-notfound">
          <div className={styles.stateTitle}>Not found</div>
          <p>{notFoundMessage}</p>
          <button type="button" className={styles.stateBtn} onClick={onBack}>
            Back to Book a Session
          </button>
        </div>
      )}

      {status === 'ready' && (
        <>
          {/* Hero is pinned OUTSIDE the scroller (z-index behind it) so it stays
              static while the sheet scrolls up over it — `position: sticky` was
              unreliable under WKWebView (the image rubber-banded with content).
              A transparent spacer in the scroller is the window onto it. */}
          {hero}
          <div className={styles.scroll}>
            <div className={styles.heroSpacer} aria-hidden="true" />
            <div className={styles.sheet}>
              <div className={styles.grab} />
              <div className={styles.body}>{children}</div>
            </div>
          </div>
          {bottomBar}
        </>
      )}
    </div>
  );
}
